import catalogoData from "../../catalogo.json";
import pbaCircuitData from "../../pba_2025.json";
import { normalizeCircuitoId } from "./district-mappings";

interface CatalogoAmbito {
  nivelId: number;
  nombre: string;
  codigoAmbitos: {
    distritoId: string;
    seccionProvincialId: string;
    seccionId: string;
    municipioId: string;
    circuitoId: string;
    establecimientoId: string;
    mesaId: string;
  };
}

interface Catalogo {
  ambitos: CatalogoAmbito[];
}

const catalogo = catalogoData as Catalogo;

// Build lookup maps
const distritoNameMap = new Map<string, string>();
const seccionNameMap = new Map<string, string>(); // Key: "distritoId:seccionId"
const circuitoNameMap = new Map<string, string>(); // Key: "distritoId:circuitoId"
const circuitoToSeccionMap = new Map<string, string>(); // Key: "distritoId:circuitoId" -> seccionId
const establecimientoNameMap = new Map<string, string>(); // Key: "distritoId:circuitoId" -> first establishment name

// Populate distrito map (nivelId 10)
catalogo.ambitos
  .filter((a) => a.nivelId === 10 && a.codigoAmbitos.distritoId)
  .forEach((a) => {
    distritoNameMap.set(a.codigoAmbitos.distritoId, a.nombre);
  });

// Populate seccion/municipio map (nivelId 30)
catalogo.ambitos
  .filter((a) => a.nivelId === 30 && a.codigoAmbitos.seccionId)
  .forEach((a) => {
    const key = `${a.codigoAmbitos.distritoId}:${a.codigoAmbitos.seccionId}`;
    seccionNameMap.set(key, a.nombre);
  });

// Populate circuito map (nivelId 50) - use first entry for each circuito
catalogo.ambitos
  .filter((a) => a.nivelId === 50 && a.codigoAmbitos.circuitoId)
  .forEach((a) => {
    const key = `${a.codigoAmbitos.distritoId}:${a.codigoAmbitos.circuitoId}`;
    // Only set if not already present (keeps first entry)
    if (!circuitoNameMap.has(key)) {
      circuitoNameMap.set(key, a.nombre);
    }
    // Also store the circuito -> seccion mapping
    if (a.codigoAmbitos.seccionId && !circuitoToSeccionMap.has(key)) {
      circuitoToSeccionMap.set(key, a.codigoAmbitos.seccionId);
    }
  });

// Populate establecimiento/colegio map (nivelId 60) - use first entry for each circuito
catalogo.ambitos
  .filter((a) => a.nivelId === 60 && a.codigoAmbitos.circuitoId)
  .forEach((a) => {
    const key = `${a.codigoAmbitos.distritoId}:${a.codigoAmbitos.circuitoId}`;
    // Only set if not already present (keeps first entry)
    if (!establecimientoNameMap.has(key)) {
      establecimientoNameMap.set(key, a.nombre);
    }
  });

/**
 * Get distrito name by distrito ID
 */
export function getDistritoNameById(
  distritoId: string | null | undefined,
): string {
  if (!distritoId) return "";
  return distritoNameMap.get(distritoId) || distritoId;
}

/**
 * Get seccion/municipio name by distrito and seccion IDs
 */
export function getSeccionNameById(
  distritoId: string | null | undefined,
  seccionId: string | null | undefined,
): string {
  if (!distritoId || !seccionId) return "";

  const key = `${distritoId}:${seccionId}`;
  return seccionNameMap.get(key) || seccionId;
}

/**
 * Get circuito name by circuito ID
 * Prioritizes establishment name (nivelId 60) over circuito name (nivelId 50)
 * Returns formatted as "ESTABLISHMENT NAME (circuitoId)" or "CIRCUITO NAME (circuitoId)"
 * For Buenos Aires, falls back to pba_2025.json if not in catalog
 */
export function getCircuitoNameById(
  distritoId: string | null | undefined,
  circuitoId: string | null | undefined,
): string {
  if (!circuitoId || !distritoId) return circuitoId || "";

  const normalized = normalizeCircuitoId(circuitoId);
  const key = `${distritoId}:${normalized}`;

  // First try establishment name (nivelId 60) - most specific
  const establecimientoName = establecimientoNameMap.get(key);
  if (establecimientoName) {
    return `${establecimientoName} (${normalized})`;
  }

  // Then try circuito name (nivelId 50)
  const catalogName = circuitoNameMap.get(key);
  if (catalogName) {
    return `${catalogName} (${normalized})`;
  }

  // Fallback to PBA 2025 data for Buenos Aires
  if (distritoId === "02") {
    const circuit = pbaCircuitData.find(
      (c) => c.circuit_metadata.circuitoId === normalized,
    );
    if (circuit) {
      return `${circuit.circuit_metadata.name}`;
    }
  }

  // If no name found, just return the ID
  return normalized;
}

/**
 * Get seccion name from circuito data
 * Returns the municipality/department name based on the seccionId from circuit metadata
 * Works for all provinces using catalog data, with fallback to pba_2025.json for Buenos Aires
 */
export function getSeccionNameByCircuito(
  distritoId: string | null | undefined,
  circuitoId: string | null | undefined,
): string {
  if (!circuitoId || !distritoId) return "";

  const normalized = normalizeCircuitoId(circuitoId);

  // Try catalog first (nivelId 50 -> seccionId)
  const circuitoKey = `${distritoId}:${normalized}`;
  const seccionId = circuitoToSeccionMap.get(circuitoKey);

  if (seccionId) {
    const seccionKey = `${distritoId}:${seccionId}`;
    const seccionName = seccionNameMap.get(seccionKey);
    if (seccionName) return seccionName;
  }

  // Fallback to PBA 2025 data for Buenos Aires
  if (distritoId === "02") {
    const circuit = pbaCircuitData.find(
      (c) => c.circuit_metadata.circuitoId === normalized,
    );

    if (circuit?.circuit_metadata?.seccionId) {
      const seccionKey = `${distritoId}:${circuit.circuit_metadata.seccionId}`;
      return seccionNameMap.get(seccionKey) || "";
    }
  }

  return "";
}

/**
 * Format mesa display (no friendly name available, just show the number)
 */
export function formatMesaDisplay(
  mesaNumber: string | null | undefined,
): string {
  if (!mesaNumber) return "";
  return `Mesa ${mesaNumber}`;
}
